import React, { useEffect, useState } from 'react';
import { useSubscription } from '@apollo/client';
import { Info, UploadCloud } from 'lucide-react';

import client from '@api';
import { authGroupImportAuthCsv } from '@graphql/mutations/group';
import { authGroupAuthImportResults } from '@graphql/subscription/auth';
import { ErrorCategory, handleError } from '@services/errorService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Button } from '@components/ui/button';
import { Label } from '@components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Spinner } from '@components/marble/States';
import { cn } from '@components/lib/utils';
import type { AuthGroup, GqlAuth } from '@app-types/index';

const CREATE_LIST = 'create';

interface ImportResult {
  groupId: string;
  totalItems: number;
  currentIndex: number;
  data: GqlAuth | null;
}

interface ImportCsvDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: AuthGroup[];
}

/* Live import progress, fed by the auth-import subscription. */
const ImportProgress: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [total, setTotal] = useState(0);

  const { data } = useSubscription<{
    authGroupAuthImportResults: ImportResult;
  }>(authGroupAuthImportResults);

  useEffect(() => {
    const res = data?.authGroupAuthImportResults;
    if (res) {
      setTotal(res.totalItems);
      setCurrentIndex(res.currentIndex);
    }
  }, [data]);

  const progress = Math.round((currentIndex / (total || 1)) * 100);

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1 flex items-center justify-between text-sm font-semibold text-foreground">
          <span>Importing…</span>
          <span className="tabular-nums">{progress}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-marble-bg-100">
          <div
            className="h-full rounded-full bg-marble-blue-500 transition-[width]"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <div className="flex items-start gap-2 rounded-lg bg-marble-bg-50 p-3">
        <Info className="mt-0.5 size-4 shrink-0 text-marble-bg-500" />
        <p className="text-xs text-marble-bg-700">
          You can close this window — the import continues in the background.
        </p>
      </div>
      <DialogFooter>
        <Button className="cursor-pointer" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </div>
  );
};

const ImportCsvDialog: React.FC<ImportCsvDialogProps> = ({
  open,
  onOpenChange,
  groups,
}) => {
  const [fileName, setFileName] = useState('');
  const [fileText, setFileText] = useState('');
  const [group, setGroup] = useState<string>(CREATE_LIST);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (open) {
      setFileName('');
      setFileText('');
      setGroup(CREATE_LIST);
      setLoading(false);
      setImporting(false);
    }
  }, [open]);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.addEventListener('load', (ev) => {
      const contents = ev.target?.result;
      if (typeof contents === 'string') setFileText(contents);
    });
    reader.readAsText(file);
  };

  const onImport = async (): Promise<void> => {
    setLoading(true);
    try {
      const { data } = await client().mutate({
        mutation: authGroupImportAuthCsv,
        variables: {
          input: {
            ...(group === CREATE_LIST ? {} : { authGroupId: group }),
            csv: fileText,
          },
        },
      });
      if (data?.authGroupImportAuthCsv) setImporting(true);
    } catch (error) {
      handleError(error, { category: ErrorCategory.GRAPHQL, silent: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import CSV</DialogTitle>
          <DialogDescription>
            Bulk-add creators from a CSV into a new or existing list.
          </DialogDescription>
        </DialogHeader>

        {importing ? (
          <ImportProgress onClose={() => onOpenChange(false)} />
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Import into</Label>
              <Select value={group} onValueChange={setGroup}>
                <SelectTrigger className="cursor-pointer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CREATE_LIST} className="cursor-pointer">
                    Create new list
                  </SelectItem>
                  {groups
                    .filter((g) => g.name !== 'master')
                    .map((g) => (
                      <SelectItem
                        key={g._id}
                        value={g._id}
                        className="cursor-pointer"
                      >
                        {g.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <label
              className={cn(
                'flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed px-6 py-8 text-center transition-colors',
                fileName
                  ? 'border-marble-blue-300 bg-marble-blue-50/40'
                  : 'border-border hover:border-marble-blue-300 hover:bg-marble-bg-50',
              )}
            >
              <UploadCloud className="size-6 text-marble-bg-500" />
              <span className="text-sm font-semibold text-foreground">
                {fileName || 'Select a CSV file to import'}
              </span>
              <span className="text-xs text-muted-foreground">
                {fileName ? 'Click to choose a different file' : '.csv only'}
              </span>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={onPickFile}
              />
            </label>

            <DialogFooter>
              <Button
                variant="ghost"
                className="cursor-pointer"
                disabled={loading}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                className="cursor-pointer disabled:cursor-not-allowed"
                disabled={!fileText || loading}
                onClick={onImport}
              >
                {loading ? <Spinner className="size-4" /> : 'Import'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportCsvDialog;
